import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAuth from '../auth/useAuth';
import SearchField, { isSearchReady } from '../components/SearchField';
import AdminUserBrowser from '../components/AdminUserBrowser';
import AdminDepartmentRow from '../components/AdminDepartmentRow';
import PagedPicker from '../components/PagedPicker';

import PageLayout from '../components/PageLayout';
import AccountCard from '../components/AccountCard';
import AdminCreateUserForm from '../components/AdminCreateUserForm';
import AdminCreateDepartmentForm from '../components/AdminCreateDepartmentForm';
import AdminCreateGroupForm from '../components/AdminCreateGroupForm';
import AdminGroupMatrix from '../components/AdminGroupMatrix';
import AdminIdpMappings from '../components/AdminIdpMappings';
import AdminSecuritySettings from '../components/AdminSecuritySettings';
import AdminUserRow from '../components/AdminUserRow';
import {
  getAdminDepartments,
  getAdminGroups,
  getAdminRights,
  getAdminUserScope,
  getAdminUsers,
} from '../api';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';

function AdminPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // The rows below name each listed account `user`; this is the caller.
  const currentUserId = user?.userId;

  // A block appears only when its right is held, and the check matches the one
  // the endpoint behind it makes: platform-wide for the ones that shape the
  // platform, "anywhere" for managing people. An administrator of one
  // department opens this page and finds the user block alone.
  const platformRights = user?.rights ?? [];
  const anyRights = user?.rightsAnywhere ?? [];

  const canManageUsers = anyRights.includes('users.manage');
  const canManageGroups = platformRights.includes('groups.manage');
  const canManageDepartments = platformRights.includes('departments.manage');
  const canManageSecurity = platformRights.includes('admin.access');

  // "First" means the first block this person actually sees: a department
  // administrator opens the page on "Create new user", not on a gap where the
  // department block would have been.
  const firstCard = [
    ['admin-users-title', canManageUsers],
    ['admin-group-rights-title', canManageGroups],
    ['admin-departments-title', canManageDepartments],
    ['admin-security-title', canManageSecurity],
  ].find(([, visible]) => visible)?.[0];

  const [users, setUsers] = useState([]);
  const [userScope, setUserScope] = useState(null);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [rights, setRights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Rights are granted to one person at a time: the list is a way to find that
  // person, not something to browse. Nothing is listed until a search narrows
  // it, and opening someone hides everyone else.
  const [expandedUserId, setExpandedUserId] = useState(null);

  // ...but sometimes the question is "who is there at all", and then the whole
  // list has to be reachable. That is a different shape - pages, not results -
  // so it is a different mode rather than a search that matches everything.
  const [isBrowsingUsers, setBrowsingUsers] = useState(false);

  // Departments work the same way: find one, open it, see what it holds.
  const [departmentQuery, setDepartmentQuery] = useState('');
  const [isBrowsingDepartments, setBrowsingDepartments] = useState(false);
  const [pickedDepartmentIds, setPickedDepartmentIds] = useState([]);
  const [expandedDepartmentId, setExpandedDepartmentId] = useState(null);

  const refreshUsers = useCallback(async () => {
    try {
      setLoadError('');

      // The server already lists only the accounts this person may see; the
      // scope says which groups they may hand out.
      const [
        usersResponse,
        scopeResponse,
        groupsResponse,
        departmentsResponse,
        rightsResponse,
      ] =
        await Promise.all([
          canManageUsers ? getAdminUsers() : [],
          canManageUsers ? getAdminUserScope() : null,
          getAdminGroups(),
          getAdminDepartments(),
          getAdminRights(),
        ]);

      setUsers(usersResponse ?? []);
      setUserScope(scopeResponse);
      setGroups(groupsResponse ?? []);
      setDepartments(departmentsResponse ?? []);
      setRights(rightsResponse ?? []);
    } catch (loadingError) {
      console.error(
        'Failed to load admin data:',
        loadingError,
      );

      setLoadError(
        loadingError.message ||
        t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [t, canManageUsers]);

  // A department administrator hands out groups of their own departments only,
  // never a platform group - the same rule POST/PATCH /admin/users enforce.
  const assignableGroups = userScope?.platform
    ? groups
    : groups.filter(
      (group) =>
        !group.is_platform &&
        (userScope?.departmentIds ?? []).includes(group.department_id),
    );

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const normalizedQuery = userSearchQuery.trim().toLowerCase();

  // One or two characters match almost everyone, so the list stays empty until
  // the search says something - the same rule the group matrix follows.
  const isSearchingUsers = isSearchReady(userSearchQuery);

  const matchingUsers = isSearchingUsers
    ? users.filter((user) => {
      const haystack = [
        user.firstName,
        user.lastName,
        user.username,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    })
    : [];

  const expandedUser = expandedUserId
    ? users.find((user) => user.userId === expandedUserId)
    : null;

  // An open user is shown alone, even if the search text no longer matches
  // them — closing the row brings the result list back.
  const visibleUsers = expandedUser ? [expandedUser] : matchingUsers;

  const isSearchingDepartments = isSearchReady(departmentQuery);

  const matchingDepartments = isSearchingDepartments
    ? departments.filter((department) =>
      department.name
        .toLowerCase()
        .includes(departmentQuery.trim().toLowerCase()),
    )
    : [];

  // Browsing wins over searching, as in the group matrix: what was picked from
  // the list is an explicit choice and a stale query should not narrow it.
  const visibleDepartments = isBrowsingDepartments
    ? departments.filter((department) =>
      pickedDepartmentIds.includes(department.departmentId),
    )
    : matchingDepartments;

  function togglePickedDepartment(departmentId) {
    setPickedDepartmentIds((current) =>
      current.includes(departmentId)
        ? current.filter((id) => id !== departmentId)
        : [...current, departmentId],
    );
  }

  return (
    <PageLayout
      extraSidebarItems={[
        {
          to: '/admin',
          label: t('adminPanel.pageTitle'),
          icon: 'fa-solid fa-user-shield',
        },
      ]}
    >
      <PageMain>
        <PageHeader
          eyebrow={
            <a href="/admin">
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              {t('adminPanel.backToAdmin')}
            </a>
          }
          title={t('adminPanel.pageTitle')}
          titleId="admin-page-title"
          description={t('adminPanel.pageDescription')}
        />

        <PageBody
          className="account-page"
          aria-labelledby="admin-page-title"
        >
          <div className="account-settings-column admin-settings-column">
            {canManageUsers && (
              <AccountCard
                titleId="admin-users-title"
                isFirst={firstCard === 'admin-users-title'}
                title={t('adminPanel.usersSectionTitle')}
                description={t('adminPanel.usersSectionDescription')}
                icon="fa-solid fa-users"
              >

                {loadError && (
                  <div
                    className="login-message login-message-error"
                    role="alert"
                  >
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{loadError}</span>
                  </div>
                )}

                {isLoading && (
                  <p>{t('adminPanel.loadingUsers')}</p>
                )}

                {!isLoading && (
                  <>
                    <div className="admin-user-search-row">
                      <SearchField
                        value={userSearchQuery}
                        onChange={setUserSearchQuery}
                        placeholder={t('adminPanel.userSearchPlaceholder')}
                        ariaLabel={t('adminPanel.userSearchAriaLabel')}
                      />

                      <Button
                        variant="secondary"
                        aria-pressed={isBrowsingUsers}
                        onClick={() =>
                          setBrowsingUsers((browsing) => !browsing)
                        }
                      >
                        <i className="fa-solid fa-list" />
                        {isBrowsingUsers
                          ? t('adminPanel.hideAllUsers')
                          : t('adminPanel.showAllUsers')}
                      </Button>
                    </div>

                    {isBrowsingUsers ? (
                      <AdminUserBrowser
                        users={users}
                        groups={groups}
                        assignableGroups={assignableGroups}
                        onChanged={refreshUsers}
                      />
                    ) : (
                      <div className="admin-user-list">
                        {visibleUsers.length === 0 ? (
                          <p className="admin-user-list-empty">
                            {isSearchingUsers
                              ? t('adminPanel.noUsersFound')
                              : t('adminPanel.searchToStart')}
                          </p>
                        ) : (
                          visibleUsers.map((user) => (
                            <AdminUserRow
                              key={user.userId}
                              user={user}
                              groups={groups}
                              assignableGroups={assignableGroups}
                              isExpanded={expandedUserId === user.userId}
                              isSelf={user.userId === currentUserId}
                              onToggle={() =>
                                setExpandedUserId((current) =>
                                  current === user.userId ? null : user.userId,
                                )
                              }
                              onChanged={refreshUsers}
                            />
                          ))
                        )}
                      </div>
                    )}

                    <AccountCard
                      titleId="admin-create-user-title"
                      title={t('adminPanel.createUserSectionTitle')}
                      description={t('adminPanel.createUserSectionDescription')}
                      icon="fa-solid fa-user-plus"
                      className="admin-create-user"
                    >
                      {createMessage && (
                        <div
                          className="login-message login-message-success"
                          role="status"
                        >
                          <i className="fa-solid fa-circle-check" />
                          <span>{createMessage}</span>
                        </div>
                      )}

                      <AdminCreateUserForm
                        groups={assignableGroups}
                        requireGroup={!userScope?.platform}
                        onCreated={(message) => {
                          setCreateMessage(message || '');
                          refreshUsers();
                        }}
                      />
                    </AccountCard>

                  </>
                )}
              </AccountCard>
            )}

            {canManageGroups && (
              <AccountCard
                titleId="admin-group-rights-title"
                isFirst={firstCard === 'admin-group-rights-title'}
                title={t('adminPanel.groupRightsSectionTitle')}
                description={t('adminPanel.groupRightsSectionDescription')}
                icon="fa-solid fa-key"
              >
                <AdminGroupMatrix
                  groups={groups}
                  rights={rights}
                  onChanged={refreshUsers}
                />

                <AccountCard
                  titleId="admin-create-group-title"
                  title={t('adminPanel.createGroupSectionTitle')}
                  description={t('adminPanel.createGroupSectionDescription')}
                  icon="fa-solid fa-user-group"
                  className="admin-create-user"
                >
                  <AdminCreateGroupForm
                    departments={departments}
                    rights={rights}
                    onCreated={refreshUsers}
                  />
                </AccountCard>

                <AccountCard
                  titleId="admin-idp-mappings-title"
                  title={t('adminPanel.idpMappingsSectionTitle')}
                  description={t('adminPanel.idpMappingsSectionDescription')}
                  icon="fa-solid fa-right-to-bracket"
                  className="admin-create-user"
                >
                  <AdminIdpMappings groups={groups} />
                </AccountCard>
              </AccountCard>
            )}

            {canManageDepartments && (
              <AccountCard
                titleId="admin-departments-title"
                isFirst={firstCard === 'admin-departments-title'}
                title={t('adminPanel.departmentsSectionTitle')}
                description={t('adminPanel.departmentsSectionDescription')}
                icon="fa-solid fa-sitemap"
              >
                <div className="admin-user-search-row">
                  <SearchField
                    value={departmentQuery}
                    onChange={setDepartmentQuery}
                    placeholder={t('adminPanel.departmentSearchPlaceholder')}
                    ariaLabel={t('adminPanel.departmentSearchAriaLabel')}
                  />

                  <Button
                    variant="secondary"
                    aria-pressed={isBrowsingDepartments}
                    onClick={() =>
                      setBrowsingDepartments((browsing) => !browsing)
                    }
                  >
                    <i className="fa-solid fa-list" />
                    {isBrowsingDepartments
                      ? t('adminPanel.hideAllUsers')
                      : t('adminPanel.showAllUsers')}
                  </Button>
                </div>

                {isBrowsingDepartments && (
                  <PagedPicker
                    items={departments}
                    getKey={(department) => department.departmentId}
                    selectedKeys={pickedDepartmentIds}
                    onToggle={togglePickedDepartment}
                    renderPrimary={(department) => department.name}
                    renderSecondary={(department) =>
                      department.isPlatform
                        ? t('adminPanel.platformScope')
                        : t('adminPanel.departmentMembers', {
                          count: department.memberCount,
                        })
                    }
                  />
                )}

                <div className="admin-user-list">
                  {visibleDepartments.length === 0 ? (
                    <p className="admin-user-list-empty">
                      {isBrowsingDepartments
                        ? t('adminPanel.pickDepartmentsToOpen')
                        : isSearchingDepartments
                          ? t('adminPanel.noDepartmentsFound')
                          : t('adminPanel.searchToStartDepartments')}
                    </p>
                  ) : (
                    visibleDepartments.map((department) => (
                      <AdminDepartmentRow
                        key={department.departmentId}
                        department={department}
                        groups={groups}
                        onChanged={refreshUsers}
                        isExpanded={
                          expandedDepartmentId === department.departmentId
                        }
                        onToggle={() =>
                          setExpandedDepartmentId((current) =>
                            current === department.departmentId
                              ? null
                              : department.departmentId,
                          )
                        }
                      />
                    ))
                  )}
                </div>

                <AccountCard
                  titleId="admin-create-department-title"
                  title={t('adminPanel.createDepartmentSectionTitle')}
                  description={t('adminPanel.createDepartmentSectionDescription')}
                  icon="fa-solid fa-plus"
                  className="admin-create-user"
                >
                  <AdminCreateDepartmentForm onCreated={refreshUsers} />
                </AccountCard>
              </AccountCard>
            )}

            {canManageSecurity && (
              <AccountCard
                titleId="admin-security-title"
                isFirst={firstCard === 'admin-security-title'}
                title={t('adminSecurity.title')}
                description={t('adminSecurity.description')}
                icon="fa-solid fa-shield-halved"
              >
                <AdminSecuritySettings />
              </AccountCard>
            )}

          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default AdminPanel;
